import React from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';


const ConversationList = ({
  conversations,
  selectedConversation,
  onSelectConversation,
  searchTerm,
  onSearchChange,
  totalUnread
}) => {
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'bg-success/10 text-success';
      case 'pending':
        return 'bg-warning/10 text-warning';
      case 'completed':
        return 'bg-muted text-muted-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Search Bar */}
      <div className="p-4 border-b border-border">
        <div className="relative">
          <Icon
            name="Search"
            size={18}
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
            strokeWidth={2}
          />
          <input
            type="text"
            placeholder="Search conversations..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e?.target?.value)}
            className="w-full pl-10 pr-4 py-2 bg-background border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        {totalUnread > 0 && (
          <div className="mt-2 flex items-center space-x-2 text-sm text-primary">
            <Icon name="Bell" size={14} strokeWidth={2} />
            <span className="font-medium">{totalUnread} unread messages</span>
          </div>
        )}
      </div>
      {/* Conversations List */}
      <div className="flex-1 overflow-y-auto">
        {conversations?.length > 0 ? (
          conversations?.map((conversation) => (
            <button
              key={conversation?.id}
              onClick={() => onSelectConversation(conversation)}
              className={`w-full p-4 border-b border-border hover:bg-muted transition-smooth text-left ${
                selectedConversation?.id === conversation?.id ? 'bg-muted' : ''
              }`}
            >
              <div className="flex items-start space-x-3">
                {/* Participant Avatar */}
                <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 bg-primary flex items-center justify-center">
                  {conversation?.participantPhoto ? (
                    <Image
                      src={conversation?.participantPhoto}
                      alt={`${conversation?.participantName} profile photo`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Icon name="User" size={24} color="white" strokeWidth={2} />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  {/* Name and Time */}
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-body font-semibold text-foreground truncate">
                      {conversation?.participantName}
                    </h3>
                    <span className="font-caption text-xs text-muted-foreground flex-shrink-0 ml-2">
                      {formatTime(conversation?.lastMessageTime)}
                    </span>
                  </div>

                  {/* Trailer Info */}
                  <div className="flex items-center space-x-2 mb-2">
                    <div className="w-8 h-8 rounded overflow-hidden flex-shrink-0">
                      <Image
                        src={conversation?.trailerImage}
                        alt={conversation?.trailerImageAlt}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <p className="font-caption text-xs text-muted-foreground truncate">
                      {conversation?.trailerName}
                    </p>
                  </div>

                  {/* Last Message */}
                  <p className={`font-body text-sm truncate mb-2 ${
                    conversation?.unreadCount > 0 ? 'font-semibold text-foreground' : 'text-muted-foreground'
                  }`}>
                    {conversation?.lastMessage}
                  </p>

                  {/* Status and Dates */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        getStatusColor(conversation?.bookingStatus)
                      }`}>
                        {conversation?.bookingStatus}
                      </span>
                      {(conversation?.bookingStatus === 'confirmed' || conversation?.bookingStatus === 'pending') && (
                        <button
                          onClick={(e) => {
                            e?.stopPropagation();
                            window.location.href = `/dispute-resolution?bookingId=${conversation?.bookingId}&conversationId=${conversation?.id}`;
                          }}
                          className="text-muted-foreground hover:text-primary transition-smooth"
                          title="File Dispute"
                        >
                          <Icon name="AlertTriangle" size={14} strokeWidth={2} />
                        </button>
                      )}
                    </div>
                    <span className="font-caption text-xs text-muted-foreground">
                      {conversation?.rentalDates}
                    </span>
                  </div>
                </div>

                {/* Unread Badge */}
                {conversation?.unreadCount > 0 && (
                  <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-bold text-primary-foreground">
                      {conversation?.unreadCount}
                    </span>
                  </div>
                )}
              </div>
            </button>
          ))
        ) : (
          <div className="p-8 text-center">
            <Icon name="MessageSquare" size={48} className="text-muted-foreground mx-auto mb-4" strokeWidth={1.5} />
            <p className="font-body text-muted-foreground">
              {searchTerm ? 'No conversations found' : 'No messages yet'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConversationList;